import { Fragment, FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { observer } from "mobx-react-lite";

import { Card } from "primereact/card";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faLanguage, faMessages, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { faExternalLink, faImages, faSignalStream, faSquareList } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { FeatureTileDto } from "../../../../@api";

import { enabledFeatures } from "../../../features";
import { Features } from "../../../features/feature.enum";
import { IFeature } from "../../../features/iFeature";
import { translationKeys } from "../../../models/app-translation-keys";
import { useFeatureTilesService } from "../../../services/ServiceHooks";
import { useStore } from "../../../stores/StoreContext";
import { TileCard } from "../../Shared/Components";

import "./LandingPage.scss";

import { normalizeLanguage, pickLanguageValue } from "../../../../utils/functions/normalizeLanguage";

const RawLandingPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const featureTilesService = useFeatureTilesService();

  const { permissionStore, userStore } = useStore();

  const landingPageTranslationsKeys = translationKeys.pages.landingPage;

  const [availableFeatures, setAvailableFeatures] = useState<IFeature[]>([]);
  const [isLoadingFeatures, setIsLoadingFeatures] = useState<boolean>(true);
  const [landingTiles, setLandingTiles] = useState<FeatureTileDto[]>([]);
  const [isLoadingLandingTiles, setIsLoadingLandingTiles] = useState<boolean>(true);

  const ICONS: Record<string, IconProp> = {
    [Features.ChatBot]: faMessages,
    [Features.ImageGeneration]: faImages,
    [Features.Transcription]: faSignalStream,
    [Features.Summary]: faSquareList,
    [Features.Translation]: faLanguage,
    [Features.SchornBox]: faExternalLink,
    externalLink: faExternalLink,
  };

  const navigateToTile = (url: string) => {
    const trimmed = url?.trim();
    if (!trimmed) {
      return;
    }

    const isExternal = /^https?:\/\//i.test(trimmed);
    if (isExternal) {
      window.open(trimmed, "_blank", "noopener,noreferrer");
      return;
    }

    const internalPath = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    navigate(internalPath);
  };

  useEffect(() => {
    setIsLoadingFeatures(true);
    let features: IFeature[] = [];
    if (permissionStore.userPermissions.roles.length) {
      features = enabledFeatures().filter(feature => permissionStore.hasRole(feature.role));
    }
    setAvailableFeatures(features);
    setIsLoadingFeatures(false);
  }, [permissionStore.userPermissions]);

  useEffect(() => {
    (async () => {
      setIsLoadingLandingTiles(true);
      try {
        const tiles = await featureTilesService.getEnabledFeatureTiles();
        setLandingTiles(tiles);
      } finally {
        setIsLoadingLandingTiles(false);
      }
    })();
  }, []);

  const orderedLandingTiles = useMemo(
    () =>
      landingTiles
        .filter(tile => tile.enabled)
        .slice()
        .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 1)),
    [landingTiles],
  );

  const featuresCardListMemo = useMemo(
    () => (
      <div className={`features features-${availableFeatures.length + orderedLandingTiles.length}`}>
        {availableFeatures.map((feature: IFeature) => (
          <Fragment key={feature.id}>{feature.card}</Fragment>
        ))}

        {orderedLandingTiles.map(tile => {
          const lang = normalizeLanguage(userStore.currentLanguage.code);
          const selectedUrl = pickLanguageValue(tile.linkUrl, lang);

          return (
            <Fragment key={tile.id ?? `tile-${tile.order ?? 0}`}>
              <TileCard
                tile={tile}
                featureIcon={tile.iconKey ? (ICONS[tile.iconKey] ?? faExternalLink) : faExternalLink}
                showAdminControls={false}
                isExternalUrl={true}
                onClick={() => navigateToTile(selectedUrl)}
                customColors={{
                  textColor: tile.colorText,
                  backgroundColor: tile.colorBackground,
                  backgroundHoverColor: tile.colorBackgroundHover,
                }}
              />
            </Fragment>
          );
        })}
      </div>
    ),
    [availableFeatures, orderedLandingTiles, userStore.currentLanguage],
  );

  const noFeaturesAvailableCardMemo = useMemo(
    () => (
      <div className="feature-list-error-card">
        <Card title={<FontAwesomeIcon icon={faTriangleExclamation} size="2x" />}>
          <h4>{t(landingPageTranslationsKeys.noAvailableFeatures.title)}</h4>

          <p>{t(landingPageTranslationsKeys.noAvailableFeatures.description)}</p>
        </Card>
      </div>
    ),
    [userStore.currentLanguage],
  );

  const backendNotAvailableMemo = useMemo(
    () => (
      <div className="feature-list-error-card">
        <Card title={<FontAwesomeIcon icon={faTriangleExclamation} size="2x" />}>
          <h4>{t(translationKeys.noBackendAvailable.title)}</h4>

          <p>{t(translationKeys.noBackendAvailable.description)}</p>
        </Card>
      </div>
    ),
    [userStore.currentLanguage],
  );

  const featureCardsMemo = useMemo(() => {
    if (isLoadingFeatures || isLoadingLandingTiles || permissionStore.isLoadingPermissions) {
      return <div>Loading ....</div>;
    }

    if (!permissionStore.hasAnyPermission()) {
      return backendNotAvailableMemo;
    }

    if (!availableFeatures.length && !orderedLandingTiles.length) {
      return noFeaturesAvailableCardMemo;
    }

    return featuresCardListMemo;
  }, [
    isLoadingFeatures,
    isLoadingLandingTiles,
    availableFeatures,
    orderedLandingTiles,
    permissionStore.userPermissions,
    userStore.currentLanguage,
  ]);

  return (
    <div className="landing-container">
      <div className="landing-page-header">
        <h1 className="landing-page-header-title">
          <span>{t(landingPageTranslationsKeys.title)}</span>{" "}
          <span className="landing-page-header-title-app">{t(translationKeys.app.title)}</span>
        </h1>

        <h3 className="landing-page-header-subtitle">{t(landingPageTranslationsKeys.subTitle)}</h3>
      </div>

      {featureCardsMemo}
    </div>
  );
};

export const LandingPage = observer(RawLandingPage);
